// Firebase Firestore imports for database operations
import { collection, getDocs, query, where } from "firebase/firestore";
// Firebase configuration and database instance
import { Firestore } from "../components/FirebaseAuth/firebase";

/**
 * Organization Site Service
 * 
 * This service provides centralized functionality for fetching all sites within an organization
 * across multiple accounts/collections. It follows service-oriented architecture principles
 * to centralize business logic and provide a clean interface for React components.
 * 
 * The service handles the complex logic of:
 * - Fetching all accounts for an organization
 * - Retrieving all sites for each account
 * - Enriching site data with account context
 * - Providing a flattened list of all organization sites
 */
export const organizationSiteService = {
  
  /**
   * Get all sites for an organization across all accounts
   * 
   * This function retrieves all sites within an organization by:
   * 1. First fetching all accounts that belong to the organization
   * 2. Then fetching all sites for each account
   * 3. Enriching each site with account metadata for context
   * 4. Returning a flattened array of all sites
   * 
   * @param {string} organizationId - The organization ID to get sites for
   * @returns {Promise<Array>} - Array of site objects with account context
   */
  async getAllSitesForOrganization(organizationId) {
    try {
      // Validate input parameter
      if (!organizationId) {
        console.warn("No organization ID provided to getAllSitesForOrganization");
        return [];
      }

      // Step 1: Fetch all accounts for this organization
      const accountsRef = collection(Firestore, "accounts");
      const accountsQuery = query(accountsRef, where("organizationId", "==", organizationId));
      const accountsSnapshot = await getDocs(accountsQuery);
      
      if (accountsSnapshot.empty) {
        console.log(`No accounts found for organization ${organizationId}`);
        return [];
      }

      // Step 2: For each account, fetch all sites and enrich with account data
      const sitePromises = accountsSnapshot.docs.map(async (accountDoc) => {
        const accountData = accountDoc.data();
        const accountId = accountDoc.id;
        
        try {
          // Fetch sites subcollection for this account
          const sitesRef = collection(Firestore, "accounts", accountId, "sites");
          const sitesSnapshot = await getDocs(sitesRef);
          
          // Filter out deleted sites and enrich each site with account context
          return sitesSnapshot.docs
            .filter((siteDoc) => {
              const siteData = siteDoc.data();
              return !siteData.status || siteData.status !== "deleted";
            })
            .map((siteDoc) => ({
              id: siteDoc.id,
              accountId: accountId,
              accountName: accountData.name || accountData.accountName || "Unnamed Account",
              organizationId: organizationId,
              ...siteDoc.data()
            }));
          
        } catch (error) {
          console.error(`Error fetching sites for account ${accountId}:`, error);
          // Return empty array for this account to not break the entire operation
          return [];
        }
      });

      // Step 3: Wait for all site fetches to complete and flatten results
      const siteArrays = await Promise.all(sitePromises);
      const allSites = siteArrays.flat();

      console.log(`Found ${allSites.length} total sites for organization ${organizationId}`);
      return allSites;
      
    } catch (error) {
      console.error(`Error fetching all sites for organization ${organizationId}:`, error);
      // Return empty array as safe default
      return [];
    }
  },

  /**
   * Get sites with enhanced metadata for display
   * 
   * This function extends getAllSitesForOrganization by adding additional
   * metadata useful for display purposes, such as formatted dates,
   * status indicators, and other computed fields.
   * 
   * @param {string} organizationId - The organization ID to get sites for
   * @returns {Promise<Array>} - Array of enriched site objects
   */
  async getSitesWithDisplayMetadata(organizationId) {
    try {
      const sites = await this.getAllSitesForOrganization(organizationId);
      
      // Enrich sites with display-friendly metadata
      return sites.map(site => ({
        ...site,
        // Add computed display fields
        displayName: site.name || site.siteName || "Unnamed Site",
        lastUpdatedFormatted: site.lastUpdated ? 
          new Date(site.lastUpdated.seconds * 1000).toLocaleDateString() : 
          "Unknown",
        statusDisplay: this.formatSiteStatus(site.status),
        // Account context for navigation
        accountPath: `/account/${site.accountId}`,
        siteDetailsPath: `/account/${site.accountId}/site-details/${site.id}`
      }));
      
    } catch (error) {
      console.error(`Error fetching sites with display metadata for organization ${organizationId}:`, error);
      return [];
    }
  },

  /**
   * Helper function to format site status for display
   * 
   * @param {string} status - Raw status from database
   * @returns {Object} - Formatted status with display properties
   */
  formatSiteStatus(status) {
    const statusMap = {
      'active': { label: 'Active', color: 'success' },
      'pending': { label: 'Pending', color: 'warning' },
      'inactive': { label: 'Inactive', color: 'error' },
      'draft': { label: 'Draft', color: 'info' },
      'production': { label: 'Production', color: 'success' }
    };
    
    return statusMap[status] || { label: status || 'Unknown', color: 'default' };
  },

  /**
   * Get site count statistics for an organization
   * 
   * @param {string} organizationId - The organization ID
   * @returns {Promise<Object>} - Site count statistics
   */
  async getOrganizationSiteStats(organizationId) {
    try {
      const sites = await this.getAllSitesForOrganization(organizationId);
      
      const stats = {
        totalSites: sites.length,
        activeSites: sites.filter(site => site.status === 'active').length,
        accountCount: new Set(sites.map(site => site.accountId)).size,
        sitesByAccount: sites.reduce((acc, site) => {
          const accountId = site.accountId;
          if (!acc[accountId]) {
            acc[accountId] = {
              accountName: site.accountName,
              count: 0
            };
          }
          acc[accountId].count++;
          return acc;
        }, {})
      };
      
      return stats;
      
    } catch (error) {
      console.error(`Error getting site stats for organization ${organizationId}:`, error);
      return {
        totalSites: 0,
        activeSites: 0,
        accountCount: 0,
        sitesByAccount: {}
      };
    }
  }
};